import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Edit2, Trash2, Zap, AlertTriangle } from "lucide-react";
import { CONTENT } from "@/constants/testIds";

import { API } from "../lib/api";
const ax = () => axios.create({ withCredentials: true });

function formatDate(dt) { try { return new Date(dt).toLocaleDateString("pt-BR") + " " + new Date(dt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); } catch { return dt; } }

export default function UrgentNewsPage() {
  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole(["autor_area", "administrador_total", "admin"]);
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", is_active: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => ax().get(`${API}/urgent-news`).then(r => setItems(r.data)).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const active = items.filter(i => i.is_active);
  const history = items.filter(i => !i.is_active);

  const openCreate = () => { setEditing(null); setForm({ title: "", content: "", is_active: true }); setShowForm(true); };
  const openEdit = (item) => { setEditing(item); setForm({ title: item.title, content: item.content, is_active: item.is_active }); setShowForm(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await ax().put(`${API}/urgent-news/${editing.item_id}`, form);
      else await ax().post(`${API}/urgent-news`, form);
      setShowForm(false); load();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir?")) return;
    await ax().delete(`${API}/urgent-news/${id}`);
    load();
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-[#2346ff]" />
          <div>
            <h1 className="text-xl font-bold font-heading text-slate-900">Informações de Última Hora</h1>
            <p className="text-sm text-slate-500">Comunicados urgentes em tempo real</p>
          </div>
        </div>
        {canEdit && (
          <Button data-testid={CONTENT.createBtn} onClick={openCreate} style={{ backgroundColor: "#2346ff" }} className="text-white gap-2">
            <Plus className="w-4 h-4" /> Nova Informação
          </Button>
        )}
      </div>

      {active.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-sm font-semibold text-red-600 uppercase tracking-wide">Ativo Agora</span>
          </div>
          <div className="space-y-3" data-testid={CONTENT.contentList}>
            {active.map(item => (
              <div key={item.item_id} data-testid={CONTENT.contentItem}
                className="rounded-xl p-5 border border-blue-200 group"
                style={{ background: "linear-gradient(135deg, #2346ff08, #2346ff15)" }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#2346ff" }}>
                      <Zap className="w-4 h-4 text-white" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-slate-900 font-heading">{item.title}</h3>
                      <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{item.content}</p>
                      <p className="text-xs text-slate-400 mt-2">Publicado em {formatDate(item.created_at)} · por {item.created_by_name}</p>
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 flex-shrink-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)}><Edit2 className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => remove(item.item_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {active.length === 0 && (
        <div className="text-center py-8 mb-6 rounded-xl bg-slate-50 border border-dashed border-slate-200">
          <Zap className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          <p className="text-slate-500 text-sm">Nenhuma informação urgente no momento</p>
        </div>
      )}

      {history.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Histórico</h2>
          <div className="space-y-2">
            {history.map(item => (
              <div key={item.item_id} className="bento-card p-3 opacity-70 group">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-slate-700">{item.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{formatDate(item.created_at)}</p>
                  </div>
                  {canEdit && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)}><Edit2 className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => remove(item.item_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              {editing ? "Editar Informação Urgente" : "Nova Informação Urgente"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Título *</Label><Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="Ex: Sistema em manutenção" /></div>
            <div><Label>Mensagem *</Label><Textarea rows={4} value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))} placeholder="Detalhe a situação urgente..." /></div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({...f, is_active: v}))} />
              <Label>Exibir como ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.title || !form.content} style={{ backgroundColor: "#2346ff" }} className="text-white">{saving ? "Salvando..." : "Publicar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
